import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { Trash2 } from 'lucide-react';

import api from 'services/api';
import { isBlob } from 'utils/utils';
import { IdParamsType } from 'utils/types';
import { FieldProps } from 'features/EventImgContent/types';
import { defaultValues, isNewImg } from 'features/EventImgContent/utils';
import { getContent } from 'features/EventImgContent/services';
import { useUploadImageMutation } from 'features/EventImgContent/useUploadImageMutation';
import 'features/common/components/Punchup/composites/editors/PunchupInlineEditor/PunchupInlineEditor.css';

import { PunchupImageUpload } from 'features/common/components/Punchup/composites/forms/PunchupImageUpload';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import {
	useAccordionForm,
	useSeededEditForm,
} from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import {
	PunchupDialogue,
	DialogIcon,
	DialogTitle,
	DialogSubtitle,
	ButtonGroup,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { queryClient } from 'index';
import { Container } from 'features/EventImgContent/EventImgContent.styles';

interface EventImageFormProps {
	onDone: () => void;
}

/**
 * EventImageForm
 *
 * Wraps the event image upload inside an accordion section.
 * All button logic (Cancel / Save / Replace / Delete) lives in PunchupImageUpload.
 * Save → flash green + onDone. Delete → confirmation dialog → API call → flash + onDone.
 */
export const EventImageForm: FC<EventImageFormProps> = ({ onDone }) => {
	const { t } = useTranslation();
	const { id } = useParams<IdParamsType>();
	const eventId = parseInt(id);

	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const { data: initialValues } = useQuery({
		queryKey: ['event-content', id],
		queryFn: () => getContent(id),
	});

	const formMethods = useForm<FieldProps>({
		defaultValues,
	});

	// Seed the form once from the saved event content, held behind a spinner until then. Seeding once
	// (not React Query's controlled `values:`) keeps a later refetch from overwriting in-progress edits.
	const { isSeeded, seedGate } = useSeededEditForm<FieldProps, FieldProps>({
		isEditMode: true,
		formMethods,
		seedData: initialValues,
		toFormValues: (data) => data,
	});

	// `isSaving` drives the self-rendered save overlay below. ImgContentSection does NOT wrap
	// this form in PunchupInlineEditor (it has no entity list — just one image), so the overlay
	// that PunchupInlineEditor would supply automatically must be rendered here, mirroring
	// GeneralSection. See src/styles/docs/form-persistence.md → "Forms without PunchupInlineEditor".
	const { flash, isSaving } = useAccordionForm(formMethods);
	const { mutateUploadImage } = useUploadImageMutation(formMethods);

	const { mutateAsync: saveImage } = useMutation({
		mutationFn: async (attributes: FieldProps) => {
			const [image] = attributes.mediaContent.eventImage;
			if (image && initialValues && isNewImg(image)) {
				await mutateUploadImage({ image, prefix: image.name, index: 1, type: 'event_image' });
			}
			const values = formMethods.getValues();
			await api.put(`/events/${eventId}`, {
				event_id: eventId,
				images: values.mediaContent.eventImage[0]?.url
					? [{ ...values.mediaContent.eventImage[0], image: values.mediaContent.eventImage[0]?.url }]
					: [],
				media: values.mediaContent.media,
			});
		},
		// Await the refetch inside onSuccess (before flashing) so isSubmitting — and therefore the
		// save overlay — stays up through the whole save→upload→refetch→close window rather than
		// vanishing the instant the PUT returns.
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['event', id] });
			await queryClient.invalidateQueries({ queryKey: ['event-content', id] });
			flash('success', { title: t('image-saved', 'Image saved'), onComplete: onDone });
		},
		onError: () => flash('error', { title: t('image-save-failed', 'Failed to save image') }),
	});

	const { mutate: deleteImage, isLoading: isDeletingImage } = useMutation({
		mutationFn: async () => {
			await api.put(`/events/${eventId}`, {
				event_id: eventId,
				images: [],
				media: formMethods.getValues('mediaContent.media') ?? [],
			});
		},
		onSuccess: () => {
			flash('success', {
				title: t('image-removed', 'Image removed'),
				onComplete: () => {
					queryClient.invalidateQueries({ queryKey: ['event', id] });
					queryClient.invalidateQueries({ queryKey: ['event-content', id] });
					onDone();
				},
			});
		},
		onError: () => flash('error', { title: t('image-delete-failed', 'Failed to remove image') }),
	});

	const handleSubmit = async (attributes: FieldProps) => {
		try {
			await saveImage(attributes);
		} catch {
			// Error handled by mutation's onError
		}
	};

	// Held behind the spinner until the saved event content is seeded.
	if (!isSeeded) return <>{seedGate}</>;

	return (
		<>
			<Container onSubmit={handleSubmit} formMethods={formMethods} isPunchup>
				<div className="entityEditSavingWrapper">
					<PunchupImageUpload
						inputId="mediaContent.eventImage"
						onCancel={onDone}
						onDelete={() => setShowDeleteConfirm(true)}
						getPreviewSrc={(entry) => (isBlob(entry.url) ? entry.url : `${entry.url}?alt=media`)}
						isSaving={isSaving}
					/>
					{isSaving && (
						<div className="entityEditSavingOverlay">
							<PunchupSpinner size={32} />
						</div>
					)}
				</div>
			</Container>

			{showDeleteConfirm &&
				ReactDOM.createPortal(
					<PunchupDialogue
						maxWidth={400}
						isLoading={isDeletingImage}
						isDestructive
						onClose={() => !isDeletingImage && setShowDeleteConfirm(false)}
					>
						<DialogIcon variant="destructive">
							<Trash2 size={24} />
						</DialogIcon>
						<DialogTitle>{t('image-delete-confirm-title', 'Remove Image')}</DialogTitle>
						<DialogSubtitle>
							{t('image-delete-confirm-body', 'This will permanently remove the show image.')}
						</DialogSubtitle>
						<ButtonGroup>
							<PunchupButton
								type="button"
								variant="secondary"
								disabled={isDeletingImage}
								onClick={() => setShowDeleteConfirm(false)}
								text={t('button-cancel')}
							/>
							<PunchupButton
								type="button"
								variant="destructive"
								disabled={isDeletingImage}
								onClick={() => deleteImage()}
								text={t('button-delete')}
							/>
						</ButtonGroup>
					</PunchupDialogue>,
					document.body
				)}
		</>
	);
};
